import { useState } from "react";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Activity, RefreshCw, ShieldCheck, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const STATUS = { ok: "bg-emerald-50 text-emerald-700 border-emerald-200", warn: "bg-amber-50 text-amber-700 border-amber-200", bad: "bg-red-50 text-red-700 border-red-200" };
const Check = ({ c }) => (
  <div className={`border rounded-md px-2 py-1.5 text-xs ${STATUS[c.status]}`} data-testid={`it-check-${c.key}`}>
    <div className="opacity-80">{c.label}</div>
    <div className="font-mono font-semibold">{c.value}</div>
  </div>
);

// SLA targets per priority (hours) with customer-tier factors and manager escalation.
export function SlaPolicy({ canEdit }) { const { t: tr } = useT();
  const q = useApi("/it/sla");
  const br = useApi("/it/sla/breaches");
  const [draft, setDraft] = useState(null);
  const d = draft || q.data;
  if (!d) return null;
  const set = (pr, i, v) => setDraft({ ...d, hours: { ...d.hours, [pr]: d.hours[pr].map((x, j) => (j === i ? Number(v) : x)) } });
  const setTier = (t, v) => setDraft({ ...d, tier_factor: { ...d.tier_factor, [t]: Number(v) } });
  const save = async () => {
    try {
      await api.put("/it/sla", d);
      toast.success(tr("lbl.sla_policy_saved"));
      setDraft(null);
      q.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="border rounded-lg bg-card p-3 mb-4 text-xs" data-testid="it-sla">
      <div className="eyebrow flex items-center gap-1 mb-2"><Timer className="h-3 w-3" />SLA policy · hours to first response / resolution</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {Object.keys(d.hours).map((pr) => (
          <div key={pr} className="border rounded-md px-2 py-1.5" data-testid={`it-sla-${pr}`}>
            <div className="font-medium capitalize mb-1">{pr}</div>
            <div className="flex items-center gap-1">
              <Input type="number" data-testid={`it-sla-${pr}-resp`} className="h-7 w-16 font-mono" disabled={!canEdit} value={d.hours[pr][0]} onChange={(e) => set(pr, 0, e.target.value)} /><span>/</span>
              <Input type="number" data-testid={`it-sla-${pr}-reso`} className="h-7 w-16 font-mono" disabled={!canEdit} value={d.hours[pr][1]} onChange={(e) => set(pr, 1, e.target.value)} /><span>h</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-2">
        <span className="text-muted-foreground">Customer tier factor:</span>
        {Object.keys(d.tier_factor).map((t) => <label key={t} className="flex items-center gap-1 capitalize">{t}<Input type="number" step="0.05" min="0.1" max="1" data-testid={`it-tier-${t}`} className="h-7 w-16 font-mono" disabled={!canEdit} value={d.tier_factor[t]} onChange={(e) => setTier(t, e.target.value)} /></label>)}
        <label className="flex items-center gap-2"><input type="checkbox" data-testid="it-sla-escalate" disabled={!canEdit} checked={!!d.escalate_to_manager} onChange={(e) => setDraft({ ...d, escalate_to_manager: e.target.checked })} />{tr("lbl.escalate_breaches_to_it_managers")}</label>
        {canEdit && draft ? <Button size="sm" className="h-7 ml-auto" data-testid="it-sla-save" onClick={save}>{tr("lbl.save_policy")}</Button> : null}
      </div>
      <div className="mt-3 eyebrow">Breached open tickets · {(br.data || []).length}</div>
      <ul className="divide-y mt-1">
        {(br.data || []).slice(0, 8).map((t) => (
          <li key={t.id} className="py-1 flex flex-wrap gap-2 items-center" data-testid={`it-breach-${t.id}`}><span className="font-mono">{t.ref}</span><span className="uppercase text-[10px] px-1 rounded bg-red-50 text-red-700">{t.priority}</span><span className="truncate max-w-xs">{t.subject}</span><span className="text-muted-foreground">{t.agent || "unassigned"}</span>{t.escalated_at ? <span className="ml-auto text-amber-700">escalated ({t.escalation_kind})</span> : <span className="ml-auto text-muted-foreground">pending escalation</span>}</li>
        ))}
      </ul>
      {(br.data || []).length === 0 ? <div className="text-muted-foreground mt-1">{tr("lbl.no_sla_breaches_right_now")}</div> : null}
    </div>
  );
}

// Webhooks, API keys, mail, backups and jobs → one health score, with one-click retry of failed webhooks.
export function IntegrationHealth() { const { t } = useT();
  const q = useApi("/it/health");
  const [busy, setBusy] = useState(false);
  const d = q.data;
  if (!d) return null;
  const retry = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/it/health/retry-webhooks");
      toast.success(`${data.requeued} failed deliveries re-queued`);
      q.reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const tone = d.score >= 80 ? "text-emerald-700" : d.score >= 60 ? "text-amber-700" : "text-red-700";
  return (
    <div className="border rounded-lg bg-card p-3 mb-4 text-xs" data-testid="it-health">
      <div className="flex flex-wrap items-center gap-3 mb-2"><span className="eyebrow flex items-center gap-1"><Activity className="h-3 w-3" />Integration health · last {d.days} days</span><span className={`font-mono text-lg font-semibold ${tone}`} data-testid="it-health-score">{d.score}/100</span>
        <Button size="sm" variant="outline" className="h-7 ml-auto" data-testid="it-retry-webhooks" disabled={busy} onClick={retry}><RefreshCw className="h-3 w-3 mr-1" />{t("lbl.retry_failed_webhooks")}</Button></div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">{d.checks.map((c) => <Check key={c.key} c={c} />)}</div>
      {d.webhooks.length > 0 ? <div className="mt-2 flex flex-wrap gap-2">{d.webhooks.map((h) => <span key={h.id} className={`border rounded px-1.5 py-0.5 ${h.failed ? "border-red-300 text-red-700" : ""}`} data-testid={`it-hook-${h.id}`}>{h.url}{h.failed ? ` · ${h.failed} failed` : ""}{!h.active ? " · inactive" : ""}</span>)}</div> : null}
    </div>
  );
}

// Security posture: 2FA, dormant users, stale sessions, failed logins, admins, old keys → score/grade.
export function SecurityPosture() { const { t } = useT();
  const q = useApi("/it/security");
  const d = q.data;
  if (!d) return null;
  const tone = d.score >= 85 ? "text-emerald-700" : d.score >= 70 ? "text-amber-700" : "text-red-700";
  return (
    <div className="border rounded-lg bg-card p-3 mb-4 text-xs" data-testid="it-security">
      <div className="flex items-center gap-3 mb-2"><span className="eyebrow flex items-center gap-1"><ShieldCheck className="h-3 w-3" />Security posture · {d.users} users</span><span className={`font-mono text-lg font-semibold ${tone}`} data-testid="it-security-score">{d.score}/100 · grade {d.grade}</span></div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">{d.checks.map((c) => <Check key={c.key} c={c} />)}</div>
    </div>
  );
}
